import * as React from "react";
import classNames from "classnames";
import { Box } from "@dropbox/dig-foundations";
import { IconSizeProvider } from "../icon-layout";
import { MetadataContext } from "../metadata";
import { TileSizeProvider, useTileSize, TilePropsContext } from "./context";
import { TileSizes } from "./types";
import { _ActionLabel } from "./ActionLabel";
import { _SubText } from "./SubText";
import css from "./Layout.module.css";

export interface LayoutProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, "color"> {
  /**
   * The size of the tile.
   * @default medium
   */
  size: TileSizes;
  className?: string;
  /**
   * The label of the tile, should be a string or a very simple React Node.
   * Will truncate content
   */
  withLabel: React.ReactNode;
  /**
   * The subtext of the tile, should be a string or a very simple React Node.
   * Will truncate content **only if** the prop is a string
   */
  withSubtext?: React.ReactNode;
  /**
   * The start accessory of the tile, ideally an icon or an avatar type of component.
   */
  withStartAccessory: React.ReactNode;
  /**
   * The end accessory of the tile, ideally used for any secondary actions of the Tile.
   */
  withEndAccessory?: React.ReactNode;
  /**
   * Will force the tile to be full width and removes the max-width constraint on the `withLabel` slot.
   */
  isFullWidth?: boolean;
  /**
   * By default end accessories are hidden when the tile is not being hovered or focused. This prop will force the end accessory to always be visible.
   * Has no effect if the `endAccessoryLayout` is set to `inline`.
   */
  shouldAlwaysShowEndAccessory?: boolean;
  /**
   * The layout of the end accessory.
   * A floating accessory does not take up any layout space within the Tile
   * A inline accessory will take up layout space within the Tile, and always be visible.
   * @default floating
   */
  endAccessoryLayout?: "floating" | "inline";
  children?: never;
}

const _Layout = React.forwardRef<HTMLSpanElement, LayoutProps>(
  (
    {
      className,
      size: _size,
      withLabel,
      withSubtext,
      withStartAccessory,
      withEndAccessory,
      isFullWidth,
      endAccessoryLayout = "floating",
      shouldAlwaysShowEndAccessory,
      ...rest
    },
    ref,
  ) => {
    const size = useTileSize({ size: _size });

    const cls = classNames(css.layoutRoot, className, {
      [css.hasEndAccessoryInline]:
        withEndAccessory && endAccessoryLayout === "inline",
      [css.hasEndAccessory]: withEndAccessory,
      [css.isFullWidth]: isFullWidth,
      [css.large]: size === "large",
      [css.medium]: size === "medium",
      [css.small]: size === "small",
    });

    return (
      <TilePropsContext.Provider
        value={{
          hasSubText: !!withSubtext,
        }}
      >
        <TileSizeProvider size={size}>
          <IconSizeProvider size={size}>
            <MetadataContext.Provider
              value={{ size: "small", withDividers: "blank" }}
            >
              <Box
                as="span"
                className={cls}
                borderRadius={size === "small" ? "Medium" : "Large"}
                color="Text Base"
                paddingRight={
                  endAccessoryLayout === "inline" && withEndAccessory
                    ? undefined
                    : "Micro Large"
                }
                borderStyle="Solid"
                position="relative"
                {...rest}
                ref={ref}
              >
                {withStartAccessory && (
                  <Box
                    as="span"
                    className={classNames(css.startAccessory, css.accessory)}
                  >
                    {withStartAccessory}
                  </Box>
                )}
                <Box as="span" className={css.labels}>
                  <Box as="span">{withLabel}</Box>
                  {withSubtext && <Box as="span">{withSubtext}</Box>}
                </Box>
                {withEndAccessory && (
                  <Box
                    as="span"
                    backgroundColor={
                      endAccessoryLayout === "floating"
                        ? "Background Base"
                        : "transparent"
                    }
                    padding={
                      endAccessoryLayout === "floating" ? "Micro XSmall" : "0"
                    }
                    borderRadius={
                      endAccessoryLayout === "floating" ? "Medium" : undefined
                    }
                    className={classNames(css.endAccessory, css.accessory, {
                      [css.alwaysShowRightAccessory]:
                        shouldAlwaysShowEndAccessory,
                      [css.endAccessoryFloating]:
                        endAccessoryLayout === "floating",
                      [css.endAccessoryInline]: endAccessoryLayout === "inline",
                    })}
                  >
                    {withEndAccessory}
                  </Box>
                )}
              </Box>
            </MetadataContext.Provider>
          </IconSizeProvider>
        </TileSizeProvider>
      </TilePropsContext.Provider>
    );
  },
);

export const Layout = Object.assign(_Layout, {
  ActionLabel: _ActionLabel,
  SubText: _SubText,
});
